import { createMonths } from "../createMonths";

/**
 * Generic function to convert backend data to table row format.
 *
 * @param {Array} inputData - Data from backend
 * @param {Object} options - Configuration options
 * @param {string} options.groupKey - Name of the property to use as group key (e.g., 'theme', 'accountParameterId')
 * @param {string} options.groupSubKey - Name of the property to use as sub-group key (e.g., 'activity')
 * @param {Object} options.additionalFields - Additional fields to add from the group item to each row
 * @param {Function} options.processRow - Custom function to process each row after creation
 * @param {Function} options.createAdditionalRows - Function to create additional derived rows
 * @param {string} options.childrenKey - Name of the property containing child items (default: 'sites')
 * @param {string} options.inputsKey - Name of the property containing inputs data (default: 'inputs')
 * @param {Function} options.getRowKey - Function to generate unique row key
 * @returns {Array} rowData
 */
export function convertBackendDataToRows(inputData, options = {}) {
  const {
    groupKey = "theme",
    groupSubKey = "",
    additionalFields = {},
    processRow = null,
    createAdditionalRows = null,
    childrenKey = "sites",
    inputsKey = "inputs",
    getRowKey = null,
  } = options;
  const rowMap = new Map();

  inputData.forEach((groupItem) => {
    const groupValue = groupItem[groupKey];
    const groupSubValue = groupSubKey ? groupItem[groupSubKey] : "";

    // Process each child item in the childrenKey array
    (groupItem[childrenKey] || []).forEach((childItem) => {
      // Generate row key using custom function or default strategy
      const key = getRowKey
        ? getRowKey(childItem, groupItem, groupValue, groupSubValue)
        : `${childItem.id}-${groupValue}${
            groupSubValue ? `-${groupSubValue}` : ""
          }`;
      if (!rowMap.has(key)) {
        // Initialize new row with months
        const months = createMonths();

        const rowData = {
          id: null, // Will be set when processing fiscal months
          site: childItem.name,
          siteId: childItem.id,
          months: months,
          fiscal_month_id_mapping: {},
        };
        // Add the group key to the row
        rowData[groupKey] = groupValue;

        // Add sub-group key if provided
        if (groupSubKey && groupSubValue) {
          rowData[groupSubKey] = groupSubValue;
        }

        // Add any additional fields from groupItem specified in options
        for (const [fieldName, sourceField] of Object.entries(
          additionalFields,
        )) {
          rowData[fieldName] = groupItem[sourceField];
        }

        rowMap.set(key, rowData);
      }

      const existingRow = rowMap.get(key);

      // Process fiscal months if they exist
      const inputs = childItem[inputsKey];
      if (inputs) {
        for (const input of inputs) {
          const id = input.id;
          const month = input.fiscal_month;
          const amount = input.amount;

          existingRow.months[month] = amount;
          existingRow.fiscal_month_id_mapping[month] = id;
        }
      }

      // Apply custom row processing if provided
      if (processRow) {
        processRow(existingRow, childItem, groupItem, key, rowMap);
      }
    });

    // Create additional rows if the function is provided
    if (createAdditionalRows) {
      createAdditionalRows(rowMap, groupItem, groupValue, groupSubValue);
    }
  });

  return Array.from(rowMap.values());
}
